import { ref, Ref, onUnmounted } from "vue";
import mapboxgl from "mapbox-gl";
import { useMyCustomer } from "seedgreen-shared/queries/customer";

interface BaseMapOptions {
	initialCenter?: [number, number];
	initialZoom?: number;
	style?: string;
}

export function useBaseMap(
	containerRef: Ref<HTMLElement | null>,
	options: BaseMapOptions = {},
) {
	const map = ref<mapboxgl.Map | null>(null);
	const isInitialized = ref(false);
	const isLoading = ref(false);

	const { myCustomer } = useMyCustomer();

	const defaultOptions = {
		initialZoom: 16,
		attributionControl: true,
		...options,
	};

	const getInitialCenter = (): [number, number] => {
		if (options.initialCenter) return options.initialCenter;
		if (myCustomer.value?.mapCenter) return myCustomer.value.mapCenter;
		return [-95, 38]; // Default to continental USA
	};

	const initializeMap = async (): Promise<mapboxgl.Map | null> => {
		if (isInitialized.value || !containerRef.value) return null;

		isLoading.value = true;

		try {
			map.value = new mapboxgl.Map({
				container: containerRef.value,
				style: defaultOptions.style,
				center: getInitialCenter(),
				zoom: defaultOptions.initialZoom,
				attributionControl: defaultOptions.attributionControl,
			});

			await new Promise<void>((resolve) => {
				map.value?.once("load", () => {
					isLoading.value = false;
					isInitialized.value = true;
					map.value?.resize();
					resolve();
				});
			});

			return map.value as mapboxgl.Map;
		} catch (error) {
			console.error("Error initializing map:", error);
			isLoading.value = false;
			return null;
		}
	};

	const cleanup = () => {
		if (map.value) {
			map.value.remove();
			map.value = null;
		}
		isInitialized.value = false;
		isLoading.value = false;
	};

	onUnmounted(cleanup);

	return {
		map,
		isInitialized,
		isLoading,
		initializeMap,
		cleanup,
	};
}
